// Civic pixel beacon. This module is bundled by `scripts/build.mjs` into a
// single self-executing browser asset published to `bryn-pixel-<env>/`. The
// exported functions exist so the behaviour is unit-testable without a DOM; the
// repo targets the ES2022 lib (no DOM types), so the browser surface is modelled
// with the minimal structural interfaces below and injected at the call site.

export type PixelPayload = {
  url: string;
  referrer: string;
  ts: number;
  // The visit's FIRST touch, replayed on every beacon after the first (BRYN-1586). `url`/`referrer`
  // above always describe the CURRENT page, so without these the campaign UTMs die on the landing
  // page and the page a visitor happens to identify on is the one that gets attributed. Absent on
  // the visit's own first beacon (its `url` already is the first touch) and whenever session
  // storage is unavailable — the server treats both the same way.
  firstUrl?: string;
  firstReferrer?: string;
};

export type PixelConfig = {
  pixelRef: string;
  endpoint: string;
};

// Config supplied via a first-party `window.__brynPixel` global rather than the
// host `<script>` data attributes. This is the tag-manager path: a GTM custom
// template can inject `pixel.js` but cannot set `data-*` attributes on the
// injected script, so it publishes the ref (and optional endpoint) on this
// global instead. Both fields fall back behind their `data-*` equivalents.
export type PixelGlobalConfig = {
  ref?: string;
  endpoint?: string;
};

// The return leg (BRYN-594, BRYN-933, BRYN-942). The serve payload the beacon retrieves
// and surfaces inline: `status` drives the poll loop; `entity` carries company-level
// attributes + active Signal labels; `individual` carries the resolved person's
// attributes + their signals, or is null when no person resolved. Each section may
// include a stable identity-stitch key — `entity.entityId` (the resolved company's id)
// and `individual.individualId` (the resolved person's id) — for identity stitching. A
// payload is written to the visitor's PostHog person by the beacon itself
// (`createPosthogSync`, BRYN-1601) — any authorised, non-`suppressed` one, `pending` included,
// which is what carries a wrongly-resolved visitor's `linkId` to the correction bridge; a
// tenant who wants their own identity semantics reads
// the same keys off `window.bryn`/`bryn:personalize` and calls `posthog.identify()` /
// `posthog.group()` on top. Display attributes and `individual` arrive only with
// `status: "resolved"`, but `entity.entityId` / `entity.domain` travel as soon as the
// visitor's Entity exists — on a `"pending"` payload too (BRYN-1393) — so an unscored visit
// is still attributable to an account. Attributes are allowlist-filtered server-side.
//
// NOTE: intentionally duplicated from `apps/pipeline-ingest/src/serve/payload.ts`
// (`PixelServePayload`). The pixel is a standalone, import-free browser bundle
// (scripts/build.mjs transpiles this single module), so it can't share the server
// type. Keep the two shapes — and the OpenAPI schema in pixelServe.ts — in sync.
export type ServeStatus = "pending" | "resolved" | "suppressed";

// Attributes live at the section's top level (BRYN-933 flatten, decision 2026-07-01);
// `signals` is the one reserved key, every other key is a display attribute string.
export type ServeSection = {
  signals: string[];
  [attribute: string]: string | string[];
};

export type ServePayload = {
  status: ServeStatus;
  entity: ServeSection;
  individual: ServeSection | null;
  /**
   * Opaque correction handle (BRYN-1382). Not a display attribute and deliberately outside both DTO
   * sections — it is plumbing that must survive the round trip to the host page, because the
   * identity-stitch recipe stores this whole payload as an analytics person property and the
   * correction depends on reading it back. Dropping it here silently disables that mechanism end to
   * end, with nothing on either side reporting an error. Present only on a resolved payload.
   */
  linkId?: string;
};

type ScriptLike = {
  getAttribute(name: string): string | null;
};

type DocumentLike = {
  referrer: string;
  currentScript?: ScriptLike | null;
  // Fallback locator for the pixel's own <script> when `currentScript` is null (see
  // `findScript`). Optional so a host that wires neither is a silent no-op.
  querySelector?: (selector: string) => ScriptLike | null;
};

type LocationLike = {
  href: string;
};

type NavigatorLike = {
  sendBeacon?(url: string, data?: unknown): boolean;
};

type FetchInit = {
  method: string;
  headers: Record<string, string>;
  body: string;
  keepalive: boolean;
};

type FetchLike = (url: string, init: FetchInit) => unknown;
type BlobFactory = (body: string, type: string) => unknown;
type BlobCtor = new (parts: string[], options?: { type?: string }) => unknown;

// Return-leg (retrieve) browser surface, modelled structurally and injected at the
// call site like the publish surface above. All optional on PixelEnv: a host that
// wires none of them simply never retrieves (the publish leg is unaffected).
//
// The DOM personalization surface (`data-bryn-*`, below) reads an element's own marker
// attribute (`getAttribute`) and either sets its text (`textContent`) or toggles the
// standard `hidden` attribute (`set`/`removeAttribute`) — no untrusted value is ever
// interpolated into a selector or written as HTML.
type ElementLike = {
  textContent: string | null;
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
};
/** The response headers the beacon reads (only `X-Bryn-Posthog-Sync`; see {@link posthogSyncAllowed}). */
type HeadersLike = { get(name: string): string | null };
type ServeResponseLike = { ok: boolean; json(): Promise<unknown>; headers?: HeadersLike };
type ServeFetchLike = (url: string) => Promise<ServeResponseLike>;
type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};
type TimerLike = (handler: () => void, ms: number) => unknown;
type DispatchLike = (type: string, detail: unknown) => void;
type PublishLike = (payload: ServePayload) => void;
type QueryAllLike = (selector: string) => ArrayLike<ElementLike>;

export type PixelEnv = {
  document: DocumentLike;
  location: LocationLike;
  navigator?: NavigatorLike;
  fetch?: FetchLike;
  blob?: BlobFactory;
  now: () => number;
  // Return leg (optional):
  serveFetch?: ServeFetchLike;
  setTimeout?: TimerLike;
  storage?: StorageLike;
  dispatch?: DispatchLike;
  publish?: PublishLike;
  queryAll?: QueryAllLike;
  /**
   * Reader for the host page's posthog-js instance (BRYN-1601). A GETTER, not the instance:
   * `window.posthog` is assigned by PostHog's own loader, which routinely runs AFTER the pixel
   * (async snippet, tag manager, a deferred bundle), so a value captured at init would be pinned
   * to `undefined` for the whole page. Read on every poll instead, which is the "check on a
   * timer" the serve loop already gives us for free.
   */
  posthog?: () => unknown;
};

type PixelGlobal = {
  document?: DocumentLike & { querySelectorAll?: (selector: string) => ArrayLike<ElementLike> };
  location?: LocationLike;
  navigator?: NavigatorLike;
  fetch?: FetchLike;
  Blob?: BlobCtor;
  setTimeout?: (handler: () => void, ms: number) => unknown;
  sessionStorage?: StorageLike;
  dispatchEvent?: (event: unknown) => boolean;
  CustomEvent?: new (type: string, init?: { detail?: unknown }) => unknown;
  // The most-recent applied serve payload, published for synchronous reads (decision 4a).
  bryn?: ServePayload;
  // First-party config global set by the tag-manager template (untrusted shape).
  __brynPixel?: unknown;
  // The host page's posthog-js instance, if it runs one (untrusted shape; see `readPosthog`).
  posthog?: unknown;
};

// Beacon path under the Civic origin. The pixel posts to `<base>/<pixelRef>`.
// The script is Civic-hosted, so when no explicit `data-endpoint` is given the
// base is derived from the host `<script src>` origin — a relative "/pixel"
// would resolve against the embedding customer page and silently lose every
// beacon, since the script runs in the customer's browser, not on Civic.
const PIXEL_PATH = "/pixel";
const ORIGIN_RE = /^(https?:\/\/[^/]+)/i;

const deriveDefaultBase = (script: ScriptLike): string | null => {
  const origin = script.getAttribute("src")?.match(ORIGIN_RE)?.[1];
  return origin ? `${origin}${PIXEL_PATH}` : null;
};

/**
 * Resolve the pixel's own `<script>` element. `document.currentScript` is the fast path
 * — it is set during a script's top-level run for a parser-inserted tag and, in
 * Blink/WebKit, for an async dynamically-injected one. Firefox reports null for an async
 * injected script, which is exactly the Google Tag Manager Custom HTML path: GTM strips
 * custom attributes off a pasted `<script>`, so the workaround appends the pixel tag in
 * JS instead (`data-bryn-pixel-ref` set via `setAttribute` survives). When `currentScript`
 * is null, fall back to finding that injected tag in the DOM — first by the ref attribute
 * it carries, then by the shared pixel `src` (for the tag-manager-template path where the
 * ref lives only on `window.__brynPixel`). Returns null when neither resolves (SSR or a
 * test import), which keeps {@link init} a silent no-op.
 */
export const findScript = (doc: DocumentLike): ScriptLike | null => {
  if (doc.currentScript) return doc.currentScript;
  const query = doc.querySelector;
  if (!query) return null;
  return (
    query.call(doc, "script[data-bryn-pixel-ref]") ??
    query.call(doc, `script[src*="${PIXEL_PATH}/pixel.js"]`) ??
    null
  );
};

/** First-party session key holding the visit's first touch, so later pages can replay it. */
const FIRST_TOUCH_KEY = "bryn:first-touch";

/** The stored first touch of the current visit. */
type FirstTouch = { firstUrl?: string; firstReferrer?: string };

/**
 * Read the visit's first touch from session storage, recording the current page as it on the
 * visit's first beacon. Returns the STORED touch, so the first beacon returns `{}` — its own `url`
 * already is the first touch and repeating it would only pad the payload.
 *
 * This is the pixel's counterpart to PostHog's `$initial_*` person properties, and the server reads
 * it at the same precedence. `sessionStorage`, not `localStorage`, is deliberate: attribution
 * should follow one visit rather than persist indefinitely on the visitor's device, which is also
 * why there is no expiry to get wrong. The known limit is that it is per-tab — a visitor who opens
 * the landing page and then a second tab loses the first touch in that tab, which is strictly
 * better than today (where it is lost on the second *page*), and fixing it would mean
 * `localStorage` plus an explicit expiry: a different privacy question, not a bigger patch.
 *
 * Every storage access is wrapped: in a private window, with site data blocked, or over quota,
 * both `getItem` and `setItem` throw, and a beacon that reports the current page is always better
 * than a script that breaks the customer's page.
 */
export const readFirstTouch = (
  storage: StorageLike | undefined,
  current: { url: string; referrer: string },
): FirstTouch => {
  if (!storage) return {};
  try {
    const stored = storage.getItem(FIRST_TOUCH_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as unknown;
      // Untrusted: another script on the customer's page can write this key. Only strings are
      // carried through, and the server sanitizes them like any other URL it is handed.
      const record =
        typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
      const firstUrl = typeof record.url === "string" ? record.url : undefined;
      const firstReferrer = typeof record.referrer === "string" ? record.referrer : undefined;
      // A stored blob with no usable URL is treated as absent and re-recorded below.
      if (firstUrl !== undefined) {
        return { firstUrl, ...(firstReferrer === undefined ? {} : { firstReferrer }) };
      }
    }
    storage.setItem(
      FIRST_TOUCH_KEY,
      JSON.stringify({ url: current.url, referrer: current.referrer }),
    );
  } catch {
    // Storage unavailable, blocked, or full — non-fatal; this beacon reports the current page only.
  }
  return {};
};

/**
 * Builds the beacon payload from the current page URL, referrer, and timestamp, plus the visit's
 * first touch replayed from session storage (absent on the visit's first beacon).
 */
export const buildPayload = (
  location: LocationLike,
  document: DocumentLike,
  now: number,
  storage?: StorageLike,
): PixelPayload => {
  const current = { url: location.href, referrer: document.referrer };
  return { ...current, ts: now, ...readFirstTouch(storage, current) };
};

/**
 * Resolves the pixel ref and ingest endpoint. The host `<script>` data
 * attributes win; a `window.__brynPixel` `fallback` (the tag-manager path) fills
 * each field when its attribute is absent. So: ref is `data-bryn-pixel-ref` else
 * `fallback.ref`; the base is `data-endpoint` else `fallback.endpoint` else the
 * script's own origin (`deriveDefaultBase`). Returns null — a silent no-op
 * rather than a thrown error in the page — when there is no script, no pixel
 * ref, or no absolute base (so a beacon is never posted to the embedding page's
 * origin).
 */
export const resolveConfig = (
  script: ScriptLike | null | undefined,
  fallback?: PixelGlobalConfig | null,
): PixelConfig | null => {
  if (!script) return null;
  const pixelRef = script.getAttribute("data-bryn-pixel-ref") ?? fallback?.ref;
  if (!pixelRef) return null;
  const base =
    script.getAttribute("data-endpoint") ?? fallback?.endpoint ?? deriveDefaultBase(script);
  if (!base) return null;
  const endpoint = `${stripTrailingSlashes(base)}/${encodeURIComponent(pixelRef)}`;
  return { pixelRef, endpoint };
};

// A scan instead of `/\/+$/`, whose backtracking is polynomial on adversarial input.
const stripTrailingSlashes = (value: string): string => {
  let end = value.length;
  while (end > 0 && value[end - 1] === "/") end -= 1;
  return value.slice(0, end);
};

/**
 * Prefers navigator.sendBeacon (survives page unload); falls back to a
 * keepalive fetch. Returns whether the payload was handed off to either.
 */
export const send = (env: PixelEnv, endpoint: string, payload: PixelPayload): boolean => {
  const body = JSON.stringify(payload);
  const beacon = env.navigator?.sendBeacon;
  if (beacon) {
    const data = env.blob ? env.blob(body, "application/json") : body;
    if (beacon.call(env.navigator, endpoint, data)) return true;
  }
  if (env.fetch) {
    env.fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
    return true;
  }
  return false;
};

// ---- Return leg (retrieve channel, BRYN-594) ----

/** The serve endpoint for a config: the publish endpoint plus `/serve`. */
export const retrieveUrl = (config: PixelConfig): string => `${config.endpoint}/serve`;

// Poll-with-backoff (decision 2026-06-23: same-session delivery; BRYN-1008: keep
// polling after resolution). Polling starts shortly after the first publish and backs
// off exponentially until it settles into a steady cadence (RETRIEVE_STEADY_MS). Until
// the first `resolved` response the loop is bounded by RETRIEVE_MAX_PENDING_ATTEMPTS, so
// a visitor that never resolves stops instead of polling for the whole tab lifetime: a
// visitor with no resolvable identity, a tenant with the return leg disabled (serves
// `pending` by design), or one still awaiting enrichment past the window — including one whose
// pending payload already carries the account stitch key (BRYN-1393), which is deliberately not
// treated as a resolution. The server only
// returns `resolved` once there is displayable data (BRYN-1008), so this window must cover
// the resolve+enrich latency; it is a deliberate trade-off (higher = catch slower first
// resolutions same-session, lower = less futile polling from the majority of visitors who
// never resolve). Once a `resolved` payload arrives the cap is lifted and polling continues
// indefinitely, so late-arriving enrichment and Signals keep flowing. It also stops on a
// terminal `suppressed` status or page unload.
const RETRIEVE_START_MS = 1000;
const RETRIEVE_BASE_MS = 1000;
const RETRIEVE_STEADY_MS = 20000;
// Cap on consecutive pre-resolution polls (~2.5 min at the steady cadence); lifted once a
// `resolved` payload is seen. Sized to cover resolve+enrich latency without unbounded
// polling from never-resolving visitors — tune with serve load / enrichment timing.
const RETRIEVE_MAX_PENDING_ATTEMPTS = 12;

/** Exponential backoff for poll attempt `n` (0-based), capped at `maxMs`. */
export const backoffDelay = (attempt: number, baseMs: number, maxMs: number): number =>
  Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt));

/** Plain-object guard (local, since the pixel bundle imports nothing). */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Narrow the untrusted `window.__brynPixel` global to a {@link PixelGlobalConfig},
 * keeping only string `ref`/`endpoint`. Returns undefined when it isn't a usable
 * object, so a bad value is simply ignored rather than breaking resolution.
 */
const readGlobalConfig = (value: unknown): PixelGlobalConfig | undefined => {
  if (!isRecord(value)) return undefined;
  const config: PixelGlobalConfig = {};
  if (typeof value.ref === "string") config.ref = value.ref;
  if (typeof value.endpoint === "string") config.endpoint = value.endpoint;
  return config;
};

/** A conservative guard for one bryn-path segment: a plain identifier, never a prototype key. */
const isSafePathSegment = (segment: string): boolean =>
  /^[\w-]+$/.test(segment) && !FORBIDDEN_KEYS.has(segment);

// `suppressed` is the sole terminal status (BRYN-1008): the poll stops and caches on it.
// `resolved` is intentionally NOT terminal, so the pixel keeps polling for fresher
// entity/individual data; `pending` is the cold, keep-polling state.
const isTerminalStatus = (status: ServeStatus): boolean => status === "suppressed";

/**
 * Keys never copied from the (untrusted) serve JSON onto a plain object — a
 * prototype-pollution guard. The serve response is same-origin from Bryn, but the
 * beacon treats it defensively; these keys are never legitimate DTO attribute names.
 */
const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/** Narrow an unknown JSON value to a {@link ServeSection}, keeping only string attributes/signals. */
const parseSection = (raw: unknown): ServeSection => {
  const attributes: Record<string, string> = {};
  if (isRecord(raw)) {
    for (const key of Object.keys(raw)) {
      // `signals` is the reserved list key, not an attribute; FORBIDDEN_KEYS guards prototype pollution.
      if (key === "signals" || FORBIDDEN_KEYS.has(key)) continue;
      const value = raw[key];
      if (typeof value === "string") attributes[key] = value;
    }
  }
  const rawSignals = isRecord(raw) ? raw.signals : undefined;
  const signals = Array.isArray(rawSignals)
    ? rawSignals.filter((s): s is string => typeof s === "string")
    : [];
  return { ...attributes, signals };
};

/** Narrow an unknown JSON value to a {@link ServePayload}, or null when it isn't one. */
export const parseServePayload = (raw: unknown): ServePayload | null => {
  if (!isRecord(raw)) return null;
  if (raw.status !== "pending" && raw.status !== "resolved" && raw.status !== "suppressed")
    return null;
  const individual = isRecord(raw.individual) ? parseSection(raw.individual) : null;
  // `linkId` rides through unparsed into the published payload: the host page never renders it, but
  // the identity stitch forwards this object verbatim and the correction bridge reads it back.
  const linkId =
    typeof raw.linkId === "string" && raw.linkId.length > 0 ? { linkId: raw.linkId } : {};
  return { status: raw.status, entity: parseSection(raw.entity), individual, ...linkId };
};

// ---- DOM personalization via the `data-bryn-*` attribute vocabulary (BRYN-594) ----
//
// One learnable pattern rather than a documented attribute per field: every marker's
// value is a **bryn path** into the serve payload. The grammar (dot-separated):
//
//   [entity.|individual.]<attribute>     an attribute string  e.g. entity.companyName, firstName
//   [entity.|individual.]signals         the active-signal list for that level
//   [entity.|individual.]signals.<type>  membership — is <type> an active signal at that level
//   status                               the resolution status ("pending"|"resolved"|"suppressed")
//
// A bare (unprefixed) path searches both sections, individual first. Three attributes
// consume a path:
//
//   data-bryn-field="<path>"       set the element's text to the string the path resolves to
//   data-bryn-show="<condition>"   show the element only while the condition holds
//   data-bryn-hide="<condition>"   hide the element while the condition holds
//
// A <condition> is a bare path (present / non-empty / a true membership ⇒ true) or
// `<path>=<value>` (equality: a string match, or list membership). Show/hide toggle the
// standard `hidden` attribute, so the host authors the default state in markup and the
// beacon reveals or hides it once identity resolves (progressive enhancement).

const FIELD_ATTR = "data-bryn-field";
const SHOW_ATTR = "data-bryn-show";
const HIDE_ATTR = "data-bryn-hide";
// Search order for an unprefixed path: individual before entity, so the person wins any
// key collision with the company (matches the pre-BRYN-933 "individual renders last" rule).
const SEARCH_SECTIONS = ["individual", "entity"] as const;

/** Pick a named section off the payload; `individual` may be null. */
const sectionOf = (payload: ServePayload, name: string): ServeSection | null =>
  name === "entity" ? payload.entity : name === "individual" ? payload.individual : null;

/** A string display attribute (never the reserved `signals` list) off one section, or null. */
const attributeOf = (section: ServeSection, key: string): string | null => {
  if (key === "signals") return null;
  const value = section[key];
  return typeof value === "string" ? value : null;
};

// A resolved bryn path: a string attribute (`value`, also `status`), a signals list, or a
// signal-membership boolean. `null` from resolvePath means malformed / points at nothing.
type PathResult =
  | { kind: "value"; value: string }
  | { kind: "list"; list: string[] }
  | { kind: "bool"; value: boolean };

/**
 * Resolve a bryn path against the payload. Reading (never writing) payload fields by key,
 * with the prototype-key guard on every segment, keeps a crafted path (`__proto__`, …)
 * from traversing object internals.
 */
export const resolvePath = (payload: ServePayload, path: string): PathResult | null => {
  const segments = path.split(".");
  if (!segments.every(isSafePathSegment)) return null;

  // Top-level `status` — a scalar, so only the bare `status` path.
  if (segments[0] === "status")
    return segments.length === 1 ? { kind: "value", value: payload.status } : null;

  // Optional section prefix; otherwise search both sections (individual first).
  const scoped = segments[0] === "entity" || segments[0] === "individual";
  const rest = scoped ? segments.slice(1) : segments;
  if (rest.length === 0 || rest.length > 2) return null;
  const names: readonly string[] = scoped ? [segments[0]] : SEARCH_SECTIONS;

  if (rest[0] === "signals") {
    if (rest.length === 2) {
      const type = rest[1];
      const present = names.some((n) => sectionOf(payload, n)?.signals.includes(type) ?? false);
      return { kind: "bool", value: present };
    }
    const list: string[] = [];
    for (const n of names) list.push(...(sectionOf(payload, n)?.signals ?? []));
    return { kind: "list", list };
  }

  if (rest.length !== 1) return null; // an attribute is a single trailing segment
  for (const n of names) {
    const section = sectionOf(payload, n);
    const value = section && attributeOf(section, rest[0]);
    if (value != null) return { kind: "value", value };
  }
  return null;
};

/** The string a `data-bryn-field` path resolves to, or null (then leave the host default). */
export const resolveFieldValue = (payload: ServePayload, path: string): string | null => {
  const result = resolvePath(payload, path);
  return result?.kind === "value" ? result.value : null;
};

/**
 * Evaluate a `data-bryn-show` / `data-bryn-hide` condition. A bare `<path>` is a presence
 * test (non-empty string, non-empty list, or a true membership); `<path>=<value>` is an
 * equality test (string match or list membership). Malformed / missing ⇒ false.
 */
export const evalCondition = (payload: ServePayload, condition: string): boolean => {
  const eq = condition.indexOf("=");
  const path = (eq === -1 ? condition : condition.slice(0, eq)).trim();
  const expected = eq === -1 ? null : condition.slice(eq + 1);
  const result = resolvePath(payload, path);
  if (!result) return false;
  if (expected === null) {
    if (result.kind === "value") return result.value.length > 0;
    if (result.kind === "list") return result.list.length > 0;
    return result.value;
  }
  if (result.kind === "value") return result.value === expected;
  if (result.kind === "list") return result.list.includes(expected);
  return result.value === (expected === "true");
};

/** Toggle the standard `hidden` attribute — XSS-free, CSS-overridable, no inline style. */
const setHidden = (el: ElementLike, hidden: boolean): void => {
  if (hidden) el.setAttribute("hidden", "");
  else el.removeAttribute("hidden");
};

/**
 * Apply `fn` to every element carrying `marker`, reading the marker's own value. Errors are
 * swallowed per element (one bad attribute never breaks the rest) and per family (an
 * unsupported/throwing `queryAll` never breaks the page).
 */
const eachMarked = (
  env: PixelEnv,
  marker: string,
  fn: (el: ElementLike, value: string) => void,
): void => {
  if (!env.queryAll) return;
  try {
    const nodes = env.queryAll(`[${marker}]`);
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      const value = el.getAttribute(marker);
      if (value == null) continue;
      try {
        fn(el, value);
      } catch {
        // One malformed marker must not break personalization for the others.
      }
    }
  } catch {
    // queryAll unsupported or threw — skip this family, never break the page.
  }
};

// The host's original field text, captured per element the first time we personalize it.
// Continuous polling (BRYN-1008) can apply a terminal `suppressed` after a `resolved` — the
// "stop showing / don't leak" path — or a later payload that drops a field. In both cases
// the field path stops resolving, and without the captured default we would leave stale
// personalized text on screen. Keyed by element identity, so it never mutates host markup.
const fieldDefaults = new WeakMap<ElementLike, string>();

/**
 * Apply a payload to the host DOM through the `data-bryn-*` vocabulary: inject field text,
 * then toggle `data-bryn-show` / `data-bryn-hide` elements. A no-op when the host wired no
 * `queryAll` (publisher-only installs).
 */
const applyDom = (env: PixelEnv, payload: ServePayload): void => {
  eachMarked(env, FIELD_ATTR, (el, path) => {
    const value = resolveFieldValue(payload, path);
    if (value != null) {
      // Capture the host default once, before the first overwrite, so it can be restored.
      if (!fieldDefaults.has(el)) fieldDefaults.set(el, el.textContent ?? "");
      el.textContent = value;
    } else {
      // No value resolves for this path (a suppressed payload, or a field dropped from a
      // later one): restore the host default rather than leaving stale personalized text.
      const original = fieldDefaults.get(el);
      if (original !== undefined) el.textContent = original;
    }
  });
  eachMarked(env, SHOW_ATTR, (el, condition) => setHidden(el, !evalCondition(payload, condition)));
  eachMarked(env, HIDE_ATTR, (el, condition) => setHidden(el, evalCondition(payload, condition)));
};

/**
 * Surface a retrieved payload three ways (spec §17.4): apply it to the DOM via the
 * `data-bryn-*` attribute vocabulary (field text + show/hide — see `applyDom`), publish
 * the latest payload to the `window.bryn` DOM global for synchronous reads (decision 4a),
 * and dispatch a `bryn:personalize` CustomEvent (observer mode). All three fire on every
 * applied payload — the observer and global paths are what let the page upgrade after
 * load (decision 2026-06-23).
 */
export const applyServePayload = (env: PixelEnv, payload: ServePayload): void => {
  applyDom(env, payload);
  // Best-effort: a host page that already defines `window.bryn` as a non-writable or
  // accessor property would throw on assignment. Isolate the new sink so a global-name
  // collision can't break the event path below or the poll/cache flow in startRetrieve.
  try {
    env.publish?.(payload);
  } catch {
    // Non-fatal — the render and event surfaces still fire.
  }
  if (env.dispatch) env.dispatch("bryn:personalize", payload);
};

// ---- PostHog correlation, on by default (BRYN-1601) ----
//
// Surfacing the resolution on the page (above) and leaving the analytics write to a snippet
// the tenant has to author meant that, out of the box, NOTHING was correlated: on civic.com, 0
// of the single-page landing visits carried any `bryn` data in PostHog. And with nothing on the
// PostHog person, the return leg has nothing to echo back — `bryn.*` signal-mapping paths and
// the `linkId` correction bridge (BRYN-1382) both stay inert. So the beacon now performs the
// write itself when the host page runs posthog-js.
//
// `setPersonProperties`, never `identify()`: it attaches Bryn's data to whoever PostHog already
// thinks the visitor is without touching `distinct_id`, so it cannot clobber a tenant's own
// identification. This is the same call Bryn's own app makes
// (`apps/control-plane-ui/src/lib/analytics/bryn-stitch.ts`). The payload is forwarded WHOLE
// under one `bryn` key — filtering it to an allowlist here would silently disarm the correction
// bridge, which reads `bryn.linkId` back off the forwarded event.
//
// Three PostHog behaviours this design rests on, verified against their docs and, where the docs
// are silent, against the shipped posthog-js source rather than assumed:
//
//   1. `setPersonProperties` CREATES a person profile even under the default
//      `person_profiles: "identified_only"` — https://posthog.com/docs/data/persons lists it as
//      one of three ways a profile is created, and posthog-js implements its own
//      `createPersonProfile()` AS a `setPersonProperties({}, {})` call. So a company-only
//      resolution on an anonymous visitor lands.
//   2. It creates an ANONYMOUS person, not an identified one. Only `identify()` writes
//      `$user_state = "identified"`; `setPersonProperties` leaves it `"anonymous"`, so a later
//      `identify()` still takes its full path and fires `$identify` with `$anon_distinct_id` —
//      the backend then merges our person into theirs. This is what makes the write safe to
//      perform before the tenant has identified anyone.
//   3. That merge keeps "the surviving (identified) person's value" on a key both hold
//      (https://posthog.com/docs/product-analytics/identify). So a returning visitor whose
//      identified person already carries a STALE `bryn` would keep the stale one. `flush`
//      therefore re-writes whenever the distinct id changes under it, which is exactly the
//      post-identify moment.
//
// ## Cost: this changes the tenant's PostHog bill
//
// Read this before widening or narrowing the write condition, because it is the only real
// argument about where that condition belongs.
//
// PostHog prices anonymous events below identified ones — "anonymous events can be up to 4x
// cheaper than identified ones" (https://posthog.com/docs/data/anonymous-vs-identified-events).
// An event is identified when it carries `$process_person_profile: true`.
//
// A `setPersonProperties` call does not just make ITS OWN `$set` event identified. In posthog-js
// it routes through `_requirePersonProcessing`, which registers a PERSISTED `$epp: true` super
// property; `_hasPersonProcessing()` reads that back, and `_calculate_event_properties` stamps
// `$process_person_profile` onto every event from then on. The flag lives in the browser's
// persistence, so it survives reloads and later visits until site data is cleared.
//
// So one write moves that browser's entire event stream — past this point, and in future
// sessions — onto identified pricing. Writing on any payload that names an account (the
// condition above) means that happens for close to every pixel visitor, including ones who never
// resolve to anything useful. That is a deliberate v1 decision: correlation coverage was judged
// worth the cost, and the cost is bounded by three things rather than by the write condition —
// `Tenant.pixelPosthogSyncEnabled`, the `PIXEL_POSTHOG_SYNC` operator kill switch, and
// POSTHOG_MAX_WRITES below. Do not remove those without re-opening this trade-off.

/**
 * The posthog-js surface the correlation write needs, modelled structurally (the pixel is an
 * import-free bundle, so it cannot depend on posthog-js types). Everything optional: the host's
 * `window.posthog` is untrusted and may be a wrapper, an older version, or the loader stub.
 */
type PosthogLike = {
  setPersonProperties?: (properties: Record<string, unknown>) => void;
  get_distinct_id?: () => unknown;
  on?: (event: string, handler: (payload: unknown) => void) => unknown;
};

/** A `window.posthog` that can actually take the write. */
type PosthogWithWrite = PosthogLike & {
  setPersonProperties: (properties: Record<string, unknown>) => void;
};

/**
 * Serve-response header by which the SERVER authorises the PostHog write, per request.
 *
 * Deliberately opt-in (`on`), not opt-out (`off`): the pixel writes into the tenant's own
 * analytics account, and it is a CDN-cached asset that updates independently of the API, so
 * "absent header ⇒ write" would mean a stale pixel against an older API, or any
 * `Access-Control-Expose-Headers` regression, writes for tenants who switched it off. Opt-in
 * fails the safe way in every ordering: the worst case is the correlation not happening, which
 * is the status quo this ticket improves on, not an unwanted write into someone's PostHog.
 *
 * A header rather than a payload field because the payload is echoed verbatim into PostHog —
 * Bryn's own config has no business riding into a customer's person properties.
 */
const POSTHOG_SYNC_HEADER = "X-Bryn-Posthog-Sync";
const POSTHOG_SYNC_ON = "on";

/**
 * Whether this serve response authorises the PostHog write. Requires the explicit `on` (see
 * {@link POSTHOG_SYNC_HEADER}); a missing `headers` accessor, a missing header, or a throwing
 * one all read as "not authorised". NOTE: the header only reaches this code because the serve
 * route names it in `Access-Control-Expose-Headers` — the beacon request is cross-origin, and a
 * browser hides every non-safelisted response header from JS otherwise. Nothing on either side
 * reports an error if that is dropped; the correlation just stops happening.
 */
export const posthogSyncAllowed = (res: ServeResponseLike): boolean => {
  try {
    return res.headers?.get(POSTHOG_SYNC_HEADER)?.trim().toLowerCase() === POSTHOG_SYNC_ON;
  } catch {
    return false;
  }
};

/**
 * Ceiling on `$set` writes for one page view. Each write is a billable PostHog event, and the
 * write can feed itself: a tenant's unfiltered PostHog destination forwards our `$set` back into
 * Bryn ingest, where it is an ordinary inbound Event that can move the served payload, which
 * would move us to write again. The changed-payload/changed-identity guards below already make
 * that converge; this is the backstop that bounds it even if they don't.
 */
const POSTHOG_MAX_WRITES = 8;

/**
 * Read the host page's posthog-js instance, or undefined when there is nothing to write to
 * (yet). Called on every poll, so "not yet" is simply retried for as long as the loop runs.
 *
 * `isRecord` rejects arrays, which also and deliberately skips PostHog's own loader stub: the
 * HTML snippet installs `window.posthog = window.posthog || []` and hangs queueing methods off
 * that array until `array.js` lands. Writing through the stub would work (the queue is replayed)
 * but `get_distinct_id()` on it returns undefined, so we would not know which person we wrote to
 * and would spend a second `$set` learning. Waiting for the real instance costs one poll.
 */
const readPosthog = (env: PixelEnv): PosthogWithWrite | undefined => {
  if (!env.posthog) return undefined;
  try {
    const candidate = env.posthog();
    if (!isRecord(candidate)) return undefined;
    // The property READ is inside the try, not just the global access: `window.posthog` may be a
    // Proxy or an accessor-based wrapper, and reading a key off one of those can throw on its own.
    return typeof candidate.setPersonProperties === "function"
      ? (candidate as PosthogWithWrite)
      : undefined;
  } catch {
    // Reading the host global, or a key off it, threw — never break the page over it.
    return undefined;
  }
};

/**
 * The current PostHog person id, or undefined when it can't be read (wrapper, stub, throw).
 *
 * `get_distinct_id()` rather than `_isIdentified()`, which posthog-js also exposes. Two reasons:
 * the underscore is theirs, not ours — it is absent from the loader snippet's own method list and
 * is not part of a contract they support — and "is this person identified?" is not the question
 * that decides anything here. The write is safe either way (`setPersonProperties` never touches
 * `distinct_id`); what we need to know is WHICH person we last wrote to, so we can write again
 * when it changes. That is the id, not a boolean.
 */
const readDistinctId = (posthog: PosthogLike): string | undefined => {
  try {
    const id = posthog.get_distinct_id?.();
    return typeof id === "string" && id.length > 0 ? id : undefined;
  } catch {
    return undefined;
  }
};

/** PostHog events that mean "the person under us changed", so the `bryn` property must be re-set. */
const IDENTITY_EVENTS = new Set(["$identify", "$create_alias"]);

/**
 * Whether a payload carries anything worth correlating — the write condition (BRYN-1601,
 * decision 2026-09-09), replacing the original `status === "resolved"` gate.
 *
 * A `pending` payload counts as soon as the visitor's Entity exists: `entity.entityId` (and
 * `domain`) travel on it (BRYN-1393), and that window — attributable to an account, not yet
 * enriched — is exactly where their PostHog events would otherwise look like a separate person.
 * Waiting for `resolved` threw it away.
 *
 * How often that window is actually hit depends on the tenant: the serve leg counts one active
 * Signal as displayable, so a tenant whose pixel events map to a signal reports `resolved` on the
 * same beacon that creates the Entity and never passes through pending-with-an-id. This condition
 * is therefore about not LOSING the visitors who do sit in that window (a husk under a tenant with
 * no pixel signal mapping; a returning visitor whose identity cache lapsed), not about a second
 * write everyone gets.
 *
 * Expressed as "not the cold envelope" rather than "has an `entityId`", deliberately. The server
 * returns `emptySection()` — exactly `{ signals: [] }` — for a cold visitor and for a terminal
 * `suppressed`, and anything else it returns names the visitor somehow. Keying on one field name
 * would make the whole correlation hinge on that field surviving every future payload change, and
 * a server that stopped sending it would silently stop the write for RESOLVED visitors too — the
 * regression this ticket exists to fix. Counting keys cannot fail that way.
 *
 * `suppressed` is excluded explicitly as well as structurally: it is the "stop showing, do not
 * leak" state, and that intent should not depend on the section happening to be empty.
 */
const carriesIdentity = (payload: ServePayload): boolean => {
  if (payload.status === "suppressed") return false;
  if (payload.individual !== null) return true;
  // More than the reserved `signals` key, or a non-empty signal list ⇒ not the cold envelope.
  return Object.keys(payload.entity).length > 1 || payload.entity.signals.length > 0;
};

/**
 * The correlation sink for one page. TWO methods, and the split is load-bearing: they belong on
 * either side of the `bryn:personalize` dispatch, and three separate bugs came from getting that
 * wrong.
 *
 * **Why the dispatch is a hazard at all.** Applying a payload dispatches `bryn:personalize`; a
 * tenant listener on that event commonly calls `posthog.identify()` (Recipe 7 suggests exactly
 * that); and dispatch -> their listener -> PostHog's `$identify` -> our `eventCaptured` hook ->
 * `flush` is entirely SYNCHRONOUS. So the dispatch is a re-entry point into this sink, on the
 * host page's terms, in the middle of our own poll handling.
 *
 * `receive(payload, allowed)` therefore runs BEFORE the payload is applied, and it is where every
 * "stop" decision is made — both the response withdrawing authorisation and the payload being a
 * terminal `suppressed`. Deciding either one after the dispatch means it is not in effect for the
 * re-entry the dispatch itself causes: the stale held resolution gets written from inside the very
 * payload that was meant to stop it.
 *
 * `write(payload)` runs AFTER, which is equally deliberate: a tenant's `identify()` goes first, so
 * our write lands last, on the person they just established.
 *
 * Neither is a caller-side `if`, because the sink outlives any one response — it holds the last
 * payload and a listener that fires on the host page's schedule, so a "stop" has to reach it
 * rather than be skipped. And do not merge the two calls: a single entry point cannot be both
 * before and after the dispatch, which is the whole point.
 *
 * `stop()` is the third method and covers the case neither of the others can: the poll loop
 * ENDING. Authorisation is per response, so it is only ever as fresh as the last poll — and the
 * listener outlives the loop. Whenever polling stops (the pending-attempt ceiling, or a terminal
 * payload) the verdict can never be re-evaluated again, so the sink has to stand down instead of
 * holding a listener that would keep writing whatever it last held for the rest of the tab's
 * life. Every `return` that ends the loop must call it.
 *
 * ## The bound on the sink's authority, in one place
 *
 * Four ordering/lifetime bugs came out of this sink holding state that outlives whatever
 * authorised it, so the boundaries are enumerated here rather than left to be rediscovered:
 *
 * | Authority ends when… | Bounded by |
 * | -------------------- | ---------- |
 * | the response's verdict changes | `receive`, called per response before anything is applied |
 * | the dispatch re-enters us mid-poll | the receive/write split around `applyIfChanged` |
 * | the write re-enters us via a wrapper | the `flushing` re-entrancy flag |
 * | the poll loop ends | `stop()`, at every loop exit |
 *
 * Two `startRetrieve` exits need nothing because the sink does not exist yet at that point (no
 * `serveFetch`/`setTimeout`, and the cached-terminal short circuit, both of which return before
 * `createPosthogSync`). `write` can never run without a preceding `receive`: they are adjacent in
 * the one branch that calls them.
 *
 * KNOWN, ACCEPTED LIMIT: if the host's `setTimeout` never fires — a page that stubs it out, or a
 * tab discarded and never resumed — polling stops without reaching either loop exit, so the sink
 * keeps the last verdict indefinitely. Deliberately not guarded: a broken `setTimeout` means no
 * polling at all, hence no personalization either, and from inside the page "not yet fired" is
 * indistinguishable from "never will".
 */
export type PosthogSync = {
  readonly receive: (payload: ServePayload, allowed: boolean) => void;
  readonly write: (payload: ServePayload) => void;
  readonly stop: () => void;
};

/**
 * Build the PostHog correlation sink for one page: call it with every payload the poll retrieves
 * and it writes `{ bryn: payload }` to the visitor's PostHog person when there is something new
 * to say. Stateful per page, so it is created once per {@link startRetrieve}.
 *
 * It writes when, and only when:
 * - the payload carries something worth correlating ({@link carriesIdentity}) — any `status`,
 *   any `individual`. A `pending` payload counts as soon as the visitor's Entity exists, which is
 *   the window in which their PostHog events would otherwise look like a separate person.
 *   Company-only resolutions count too, and are the majority of reverse-IP results. Only the cold
 *   envelope and a terminal `suppressed` are excluded;
 * - a `pending` payload is not arriving after a resolution (that would downgrade the person's
 *   `bryn` from a company with attributes back to a bare id);
 * - posthog-js is actually on the page (retried on each poll until it is);
 * - the payload changed since our last write, or the distinct id under us changed (a tenant
 *   `identify()` landed — see the merge-precedence note above), or nothing has been written yet;
 * - the per-page write ceiling has not been reached.
 *
 * Every PostHog call is wrapped: a host page's posthog wrapper that throws must not take the
 * beacon's poll loop or DOM personalization down with it.
 */
export const createPosthogSync = (env: PixelEnv): PosthogSync => {
  let latest: ServePayload | undefined;
  let writtenJson: string | undefined;
  let writtenDistinctId: string | undefined;
  let writes = 0;
  let hooked = false;
  /** Teardown for the `eventCaptured` listener, when posthog-js handed one back. */
  let unhook: (() => void) | undefined;
  /**
   * Re-entrancy guard for {@link flush}.
   *
   * `setPersonProperties` is host-page code. A wrapper that identifies inside it would drive
   * PostHog's `$identify` -> our `eventCaptured` hook -> `flush` again, synchronously, from
   * INSIDE the write — and at that point `writtenJson`/`writtenDistinctId` have not been updated
   * yet (deliberately: they are set after the call returns, so a throwing write is retried rather
   * than recorded as done). The nested flush therefore sees fresh work and writes again.
   *
   * POSTHOG_MAX_WRITES does NOT bound this, which was the assumption worth checking: `writes` is
   * incremented only after the nested call returns, so the ceiling is still reading zero all the
   * way down. Measured by removing this flag: 1,897 writes before the stack ran out — the depth
   * limit was the only thing stopping it, and each frame's `catch` then swallowed the RangeError
   * on the way back up, so nothing surfaced. That is ~1,900 billable PostHog events for one
   * resolution, from inside a customer's page. This flag is the actual bound.
   */
  let flushing = false;
  /** Whether the MOST RECENT serve response authorised the write. Never assumed. */
  let authorized = false;
  // Mirrors the poll loop's own `resolvedSeen`: once a resolution has been written, a later
  // `pending` must not downgrade the person's `bryn` back to a bare id.
  let resolvedSeen = false;

  /**
   * Re-sync as soon as a tenant `identify()` lands, rather than on the next 20s poll. Cheap
   * insurance: a visitor who identifies and closes the tab inside one poll interval would
   * otherwise keep whatever stale `bryn` their identified person already had. Registered once,
   * the first time we see a real instance. `flush` filters on the event NAME, so the `$set` our
   * own write emits is ignored and the hook cannot drive itself.
   */
  const hookIdentity = (posthog: PosthogLike): void => {
    if (hooked || typeof posthog.on !== "function") return;
    hooked = true;
    try {
      const off = posthog.on("eventCaptured", (event) => {
        // This body runs inside PostHog's OWN event dispatch, so an uncaught throw here would
        // break their pipeline for the host page — the one thing the beacon must never do. Both
        // halves can throw independently: reading `event.event` off a wrapper's Proxy, and
        // `flush` itself if a future edit adds an unguarded call to it.
        try {
          const name = isRecord(event) ? event.event : undefined;
          if (typeof name === "string" && IDENTITY_EVENTS.has(name)) flush();
        } catch {
          // Swallowed: the poll still catches the identity change on its next tick.
        }
      });
      // posthog-js returns an unsubscribe; older builds and wrappers may not.
      if (typeof off === "function") unhook = off as () => void;
    } catch {
      // Hook unsupported on this posthog build — the poll still catches the id change.
    }
  };

  /**
   * Stand down completely: forget the held payload and remove the listener from the host's
   * posthog instance. Called when authorisation is withdrawn and on a terminal `suppressed`.
   *
   * Both of those mean "write nothing further", and the listener is the reason that has to be an
   * action rather than just not calling in again — it lives on the tenant's own posthog object
   * and would otherwise keep calling `flush` for the tab's lifetime. `hooked` is reset so a
   * later re-authorisation re-registers rather than running blind.
   */
  const disarm = (): void => {
    latest = undefined;
    try {
      unhook?.();
    } catch {
      // Best effort — an unsubscribe that throws must not break the poll loop.
    }
    unhook = undefined;
    hooked = false;
  };

  const flush = (): void => {
    const payload = latest;
    // `authorized` is re-checked here, not only at the entry point: `flush` is also reached from
    // the `eventCaptured` listener, which fires on the host page's schedule rather than ours.
    if (!authorized || !payload || writes >= POSTHOG_MAX_WRITES || flushing) return;
    const posthog = readPosthog(env);
    if (!posthog) return; // retried on the next poll
    hookIdentity(posthog);

    const distinctId = readDistinctId(posthog);
    const json = JSON.stringify(payload);
    // An id we have never seen is not a change: it is the first read, or the first read after a
    // write we made before the id was legible. Only a DIFFERENT known id means the person moved.
    const identityChanged =
      writtenDistinctId !== undefined &&
      distinctId !== undefined &&
      distinctId !== writtenDistinctId;
    if (json === writtenJson && !identityChanged) {
      writtenDistinctId ??= distinctId;
      return;
    }

    flushing = true;
    try {
      posthog.setPersonProperties({ bryn: payload });
    } catch {
      // Leave the state untouched so the next poll retries this same write.
      return;
    } finally {
      flushing = false;
    }
    writes += 1;
    writtenJson = json;
    writtenDistinctId = distinctId ?? writtenDistinctId;
  };

  return {
    receive: (payload, allowed) => {
      authorized = allowed;
      // BOTH stand-down reasons are decided here, before the page is told anything:
      //
      //   !allowed              the tenant switched the correlation off, or the operator kill
      //                         switch went in
      //   status "suppressed"   terminal, the server's "stop showing, do not leak"
      //
      // Each means "write nothing further", and each has to take effect before the dispatch this
      // response is about to trigger, because that dispatch can re-enter `flush` synchronously
      // via a tenant `identify()` listener. Deciding either one afterwards writes the stale held
      // resolution from inside the very payload that was meant to stop it.
      //
      // (Removing a `bryn` property already written is out of scope — this stops adding to it.)
      if (!allowed || payload.status === "suppressed") disarm();
    },

    stop: () => {
      // The poll loop has ended, so there will be no further authorisation verdict — ever. Drop
      // the held payload and detach the listener rather than leave it able to write on a verdict
      // that can no longer be refreshed. Idempotent, so every loop exit can call it.
      authorized = false;
      disarm();
    },

    write: (payload) => {
      // Re-checked, so `write` is safe to call regardless of what the caller did.
      if (!authorized) return;
      // Nothing to correlate against: a cold visitor's `pending` payload names nobody, and
      // writing it would create a billable PostHog person carrying only `status: "pending"`.
      // `carriesIdentity` also excludes `suppressed`, which `receive` has already disarmed on.
      if (!carriesIdentity(payload)) return;
      // Once a resolution has been written, ignore a later `pending` — it is routinely a
      // transient serve-error fallback, and overwriting the person's `bryn` with a bare id would
      // lose the company attributes it already holds. Mirrors the poll loop's downgrade guard.
      if (payload.status === "resolved") resolvedSeen = true;
      else if (resolvedSeen) return;
      latest = payload;
      flush();
    },
  };
};

/** First-party session cache key for a serve URL, so later pages don't re-retrieve. */
const cacheKey = (serveUrl: string): string => `bryn:serve:${serveUrl}`;

/**
 * Begin the return leg: apply a cached `suppressed` resolution if present, else poll the
 * serve endpoint with backoff that settles into a steady cadence and keeps polling
 * (BRYN-1008), so late-arriving enrichment and Signals continue to flow. Each response is
 * applied (the page shows the library default first, then upgrades on every poll); only a
 * terminal `suppressed` payload is cached and stops the loop. A `resolved` payload is
 * applied but never stops polling. A no-op when the host wired neither `serveFetch` nor
 * `setTimeout`.
 *
 * Each response also feeds the PostHog correlation sink when the response authorises it
 * (BRYN-1601) — see {@link createPosthogSync} and {@link posthogSyncAllowed}.
 */
export const startRetrieve = (env: PixelEnv, serveUrl: string): void => {
  if (!env.serveFetch || !env.setTimeout) return;
  const fetchServe = env.serveFetch;
  const schedule = env.setTimeout;

  // Apply a payload only when it differs from the last one applied (BRYN-1008). The poll
  // now runs for the tab's lifetime, so re-applying an unchanged payload would re-fire
  // `bryn:personalize` (and its listener side effects, e.g. posthog.identify/group) and
  // rewrite the DOM every cycle. Tracking the last-applied JSON keeps continuous polling
  // cheap and side-effect-free until the served data actually changes.
  let lastAppliedJson: string | undefined;
  const applyIfChanged = (payload: ServePayload): void => {
    const json = JSON.stringify(payload);
    if (json === lastAppliedJson) return;
    lastAppliedJson = json;
    applyServePayload(env, payload);
  };

  // Apply a cached terminal (suppressed) payload immediately and skip polling entirely.
  const cached = env.storage?.getItem(cacheKey(serveUrl));
  if (cached) {
    try {
      const payload = parseServePayload(JSON.parse(cached));
      if (payload && isTerminalStatus(payload.status)) {
        applyIfChanged(payload);
        return;
      }
    } catch {
      // Corrupt cache entry — ignore and poll fresh.
    }
  }

  // Bounded until the first resolution, then unbounded (BRYN-1008). A visitor that only
  // ever gets `pending` (a cold visitor who never resolves, or a return-leg-disabled tenant
  // that serves `pending` by design) stops after RETRIEVE_MAX_PENDING_ATTEMPTS rather than
  // polling for the tab's lifetime and burning that tenant's serve/rate-limit budget. Once
  // `resolvedSeen` flips, the cap is lifted so the pixel keeps polling at the steady cadence
  // for fresher entity/individual data.
  let resolvedSeen = false;
  const scheduleNext = (n: number): void => {
    if (!resolvedSeen && n + 1 >= RETRIEVE_MAX_PENDING_ATTEMPTS) {
      // Giving up on a visitor who never resolved — and the correlation sink may well be ARMED,
      // since a `pending` payload that names an account is written (BRYN-1601). With no further
      // polls the authorisation verdict can never be re-checked, so a tenant who switched the
      // correlation off would never be honoured on this tab while the identify hook kept
      // writing. Stand the sink down with the loop.
      syncPosthog.stop();
      return;
    }
    schedule(() => attempt(n + 1), backoffDelay(n, RETRIEVE_BASE_MS, RETRIEVE_STEADY_MS));
  };

  // The PostHog correlation sink (BRYN-1601), fed from the poll rather than from
  // `applyServePayload` for two reasons: it must run on every response, including the ones whose
  // payload is unchanged (that is how a late-loading posthog-js and a mid-session `identify()`
  // get picked up), and running it after `applyIfChanged` means a tenant's own
  // `bryn:personalize` listener — its `identify()`, typically — has already run, so our write
  // lands last, on the person they just established.
  const syncPosthog = createPosthogSync(env);

  const attempt = (n: number): void => {
    fetchServe(serveUrl)
      .then((res) =>
        res.ok
          ? res.json().then((raw) => ({ raw, syncAllowed: posthogSyncAllowed(res) }))
          : { raw: null as unknown, syncAllowed: false },
      )
      .then(({ raw, syncAllowed }) => {
        const payload = parseServePayload(raw);
        if (payload) {
          // Once resolved, ignore a transient `pending` (a serve-error fallback or an
          // expired/absent identity lookup): applying it would downgrade window.bryn and
          // the `bryn:personalize` event to cold and re-hide `status=resolved` elements,
          // while the already-written `data-bryn-field` text stays put, leaving the page
          // in a mixed state. Keep the personalized surfaces and keep polling for fresher
          // resolved data instead. A real "stop showing" is the terminal `suppressed`.
          // BEFORE the dispatch below, not after. `applyIfChanged` dispatches
          // `bryn:personalize`; a tenant listener on it commonly calls `posthog.identify()`, and
          // dispatch -> their listener -> `$identify` -> our hook -> `flush` is all synchronous.
          // `receive` is where every stop decision lands, so a withdrawn authorisation or a
          // terminal `suppressed` is already in effect for that re-entry rather than one step late.
          syncPosthog.receive(payload, syncAllowed);
          if (!(resolvedSeen && payload.status === "pending")) applyIfChanged(payload);
          if (payload.status === "resolved") resolvedSeen = true;
          // AFTER, so a tenant `identify()` listener has already run and our write lands last,
          // on the person they just established.
          syncPosthog.write(payload);
        }
        if (payload && isTerminalStatus(payload.status)) {
          try {
            env.storage?.setItem(cacheKey(serveUrl), JSON.stringify(payload));
          } catch {
            // Storage unavailable/full — non-fatal; the payload is already applied.
          }
          // `receive` already disarmed on the suppressed status; this makes "the loop stopped, so
          // the sink stops" hold at every exit rather than only at the one that needed it.
          syncPosthog.stop();
          return; // terminal (suppressed): stop polling, keep the cached payload
        }
        scheduleNext(n);
      })
      .catch(() => {
        // Transient failure: keep polling at the steady cadence, never break the page.
        scheduleNext(n);
      });
  };

  schedule(() => attempt(0), RETRIEVE_START_MS);
};

/**
 * Resolves config from the host script, sends one publish beacon, then arms the
 * return leg (BRYN-594): polls the serve endpoint and surfaces the payload inline.
 */
export const init = (env: PixelEnv, fallback?: PixelGlobalConfig | null): boolean => {
  const config = resolveConfig(findScript(env.document), fallback);
  if (!config) return false;
  const sent = send(
    env,
    config.endpoint,
    buildPayload(env.location, env.document, env.now(), env.storage),
  );
  startRetrieve(env, retrieveUrl(config));
  return sent;
};

/**
/**
 * Read `window.sessionStorage` without letting the page break. The property ACCESS itself throws
 * (SecurityError) in a browser configured to block site data, so this cannot be an inline
 * `global.sessionStorage` — that would take down the publish leg with it, which is exactly what
 * this module must never do. `undefined` disables the first-touch replay and the serve cache and
 * changes nothing else.
 */
const safeSessionStorage = (global: PixelGlobal): StorageLike | undefined => {
  try {
    return global.sessionStorage;
  } catch {
    return undefined;
  }
};

/**
 * Browser entry point: wires the ambient globals into {@link init}. A no-op when
 * there is no document or location (e.g. server-side rendering or a test import);
 * with a document but no resolvable pixel `<script>` (see {@link findScript}), init
 * itself no-ops.
 */
export const autostart = (global: PixelGlobal): boolean => {
  if (!global.document || !global.location) return false;
  const BlobImpl = global.Blob;
  const doc = global.document;

  // Return-leg wiring (all optional — a missing global just disables that capability).
  const rawFetch = global.fetch as unknown as
    | ((url: string, init?: Record<string, unknown>) => Promise<ServeResponseLike>)
    | undefined;
  const serveFetch: ServeFetchLike | undefined = rawFetch
    ? (url) => rawFetch(url, { method: "GET", credentials: "include", mode: "cors" })
    : undefined;
  const CustomEventImpl = global.CustomEvent;
  const dispatchEvent = global.dispatchEvent;
  const dispatch: DispatchLike | undefined =
    CustomEventImpl && dispatchEvent
      ? (type, detail) => {
          dispatchEvent.call(global, new CustomEventImpl(type, { detail }));
        }
      : undefined;
  const queryAll: QueryAllLike | undefined = doc.querySelectorAll
    ? (selector) => doc.querySelectorAll!(selector)
    : undefined;
  // Publish the latest payload to `window.bryn` for synchronous reads (decision 4a) —
  // a customer can read `window.bryn` without registering a `bryn:personalize` listener.
  const publish: PublishLike = (payload) => {
    global.bryn = payload;
  };

  return init(
    {
      document: global.document,
      location: global.location,
      navigator: global.navigator,
      fetch: global.fetch,
      blob: BlobImpl ? (body, type) => new BlobImpl([body], { type }) : undefined,
      now: () => Date.now(),
      serveFetch,
      setTimeout: global.setTimeout ? (handler, ms) => global.setTimeout!(handler, ms) : undefined,
      storage: safeSessionStorage(global),
      dispatch,
      publish,
      queryAll,
      // Read lazily on every poll — PostHog's loader commonly runs after us (BRYN-1601).
      posthog: () => global.posthog,
    },
    readGlobalConfig(global.__brynPixel),
  );
};

autostart(globalThis as unknown as PixelGlobal);
